import { browserSupportsWebAuthn, startAuthentication, startRegistration } from '@simplewebauthn/browser'
import type { AuthStateResponse, SessionUser } from '../shared/types'
import {
  ApiError,
  authenticationOptions,
  fetchAuthState,
  fetchMe,
  registrationOptions,
  verifyAuthentication,
  verifyRegistration,
} from './api'

let cached: SessionUser | null | undefined

/** One /api/me round trip per page load unless something invalidates it. */
export async function currentUser(refresh = false): Promise<SessionUser | null> {
  if (!refresh && cached !== undefined) return cached
  try {
    cached = (await fetchMe()).user
  } catch {
    cached = null
  }
  return cached
}

export function authState(): Promise<AuthStateResponse> {
  return fetchAuthState()
}

export function passkeysSupported(): boolean {
  return browserSupportsWebAuthn()
}

/** Runs the WebAuthn assertion and, on success, leaves a session cookie behind. */
export async function signInWithPasskey(): Promise<SessionUser> {
  const options = await authenticationOptions()
  const response = await startAuthentication({ optionsJSON: options })
  const result = await verifyAuthentication(response)
  cached = result.user
  return result.user
}

export async function registerPasskey(input: {
  name: string
  bootstrapCode?: string
  label?: string
}): Promise<SessionUser> {
  const options = await registrationOptions({
    bootstrapCode: input.bootstrapCode,
    label: input.label,
  })
  const response = await startRegistration({ optionsJSON: options })
  const result = await verifyRegistration({
    response,
    name: input.name,
    label: input.label,
  })
  cached = result.user
  return result.user
}

export function forgetUser(): void {
  cached = undefined
}

export async function signOut(): Promise<void> {
  try {
    await fetch('/auth/logout', { method: 'POST' })
  } finally {
    cached = null
    location.href = '/'
  }
}

/**
 * WebAuthn rejects with DOMExceptions whose names matter more than their
 * messages, which are often empty or browser-specific.
 */
export function describeWebAuthnError(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof DOMException || (error instanceof Error && error.name)) {
    switch (error.name) {
      case 'NotAllowedError':
        return 'Cancelled, or it timed out. Try again.'
      case 'InvalidStateError':
        return 'That authenticator already holds a passkey for pastabin.'
      case 'SecurityError':
        return "The site's domain doesn't match what the passkey expects."
      case 'AbortError':
        return 'Cancelled.'
      case 'NotSupportedError':
        return "That authenticator can't create the kind of passkey this app asks for."
      default:
        break
    }
  }
  return error instanceof Error && error.message ? error.message : 'Something went wrong.'
}
